/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { ChangeDetectionStrategy, Component, Input, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { SpecTemplateParagraphIs, SpecTemplateTextIs } from '@gh/builder-shared-data';
import {
  AbstractSnapshotFormComponent,
  FormState,
  FormStateBuilder,
  FormStateSnapshot,
  getFormStateSnapshotValue,
  getRouteInitData,
  toAppFormGroup,
  updateChildrenSchema,
  updateSchemaWithChildren,
  whenCreateOrEditMode,
} from '@gh/common-shared';
import { getControlValue } from '@gh/core-ui';
import { assign } from 'lodash';
import { JobSpecParagraphInfo } from '../../../resources/job.model';

const getJobSpecParagraphFormConfig = (paragraph: JobSpecParagraphInfo) => ({
  text: {
    visible: paragraph.textIs === SpecTemplateTextIs.Optional || paragraph.textIs === SpecTemplateTextIs.Mandatory,
    enabled: paragraph.isParagraphIncluded,
    required: paragraph.isParagraphIncluded && paragraph.textIs === SpecTemplateTextIs.Mandatory,
  },
  name: void 0,
  paragraphIs: { enabled: paragraph.isParagraphIncluded },
  textIs: void 0,
  isTextWarning: void 0,
  isParagraphIncluded: {
    visible: paragraph.paragraphIs === SpecTemplateParagraphIs.Optional,
  },
  isTextIncluded: {
    visible: paragraph.textIs === SpecTemplateTextIs.Optional,
    enabled: paragraph.isParagraphIncluded,
  },
});

@Component({
  standalone: false,
  selector: 'gh-job-spec-paragraph-form',
  templateUrl: 'job-spec-paragraph-form.component.html',
  styleUrls: ['job-spec-paragraph-form.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class JobSpecParagraphFormComponent extends AbstractSnapshotFormComponent<JobSpecParagraphInfo>
  implements OnInit {

  @Input() sequence: number;

  constructor(private route: ActivatedRoute,
              private formStateBuilder: FormStateBuilder) {
    super();
  }

  get hasWarningText(): boolean {
    return getControlValue(this.form.group, 'isTextWarning');
  }

  ngOnInit(): void {
    super.ngOnInit();

    assign(this, getRouteInitData(this.route));
  }

  onChange(): void {
    updateChildrenSchema(toAppFormGroup(this.form.group), getJobSpecParagraphFormConfig(this.form.submitValue));
  }

  protected buildForm(snapshot: FormStateSnapshot<JobSpecParagraphInfo>): FormState<JobSpecParagraphInfo> {
    return this.formStateBuilder.buildPlain({
      mode: this.mode,
      enabled: whenCreateOrEditMode(),
      snapshot,
      children: getJobSpecParagraphFormConfig(getFormStateSnapshotValue(snapshot)),
    });
  }

  protected afterRestored(current: FormStateSnapshot<JobSpecParagraphInfo>): void {
    updateSchemaWithChildren(toAppFormGroup(this.form.group), {
      enabled: whenCreateOrEditMode(),
      children: getJobSpecParagraphFormConfig(getFormStateSnapshotValue(current)),
    });
  }
}
